import { targetAcosEnabled } from '@/config/targetAcos.js';

// Per-page educational content ("page guides"). Rendered by
// src/components/pageGuide/PageGuide.vue in two places:
//   • inside EmptyProfile — a page with no connection / still syncing shows its guide
//     under the status banner instead of a bare overlay;
//   • the header "?" help → "About this page" drawer, available once data is synced.
//
// Keyed by route name (= the nav `to` value). Tabbed routes (ppc-analytics-tab,
// ppc-management-tab) are keyed `<route name>:<tab param>` — see pageGuideKey.
//
// Content is plain English rendered as-is, like src/data/discoverFeatures.js: the i18n
// rules forbid `$t(entry.field)`, so the copy is not routed through vue-i18n. Only the
// UI chrome around it (section headings, chips, CTAs) is translated in the components.
//
// Entry shape:
//   title     – page name as the user sees it in the nav
//   summary   – 1-2 sentences: what the page shows
//   requires  – which connection feeds it: 'ads' | 'sp' | 'none'
//   questions – 3-5 business questions the page answers
//   howTo     – 3-6 short steps, accurate to the page's actual controls
//   metrics   – optional [{ name, description }] for the key numbers
//   tips      – optional short practical notes

export const PAGE_GUIDE_REQUIRES = Object.freeze({
  ADS: 'ads',
  SP: 'sp',
  NONE: 'none',
});

// Production-nav routes that deliberately have no guide: settings, billing and help
// pages explain themselves and are never behind the "connect / syncing" empty state.
// The registry spec requires every production-nav route to be either in PAGE_GUIDES
// or listed here, so a new nav item can't silently ship without a decision.
export const PAGES_WITHOUT_GUIDE = Object.freeze([
  'ppc-discover',
  'settings-ai-preferences',
  'settings-api-keys',
  'settings-ai-connections',
  'settings-webhooks',
  'ai-assistant-how-to',
  'attribution-how-to',
  'settings-profile',
  'settings-business-profile',
  'settings-connections',
  'settings-invites',
  'billing',
]);

export const pageGuideKey = (name, tab) => (tab ? `${name}:${tab}` : name);

// Steps shared by every OLAP report page (the one-grid pages built on OlapPage).
const OLAP_PICK_SCOPE =
  'Pick a marketplace and a date range at the top. Daily, Weekly and Monthly granularity are available.';
const OLAP_SAVE_VIEW =
  'Start from a ready-made configuration, then save your own layout (grouping, columns, filters, chart) to reuse it in one click.';

const PPC_TAB_STEPS = {
  scope: 'Choose a marketplace and a date range at the top of the page.',
  edit: 'Edit values directly in the table, or select several rows to update them in bulk.',
  menu: 'Right-click a row for more actions, such as opening its search terms or change history.',
};

const PPC_TABS = {
  portfolio: {
    title: 'Management · Portfolio',
    summary:
      'Advertising performance rolled up by portfolio, so you can manage budgets and results for groups of campaigns at once.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which portfolios spend the most, and are they profitable?',
      'How is spend split across brands or product lines?',
      'Which portfolio is close to its budget cap?',
    ],
    howTo: [
      PPC_TAB_STEPS.scope,
      'Set the table View to Campaigns to see the campaigns inside each portfolio.',
      'Use the chart to compare spend, sales and ACOS over time.',
    ],
  },
  campaign: {
    title: 'Management · Campaign',
    summary:
      'Every Sponsored Products, Sponsored Brands and Sponsored Display campaign with its spend, sales and efficiency, in one editable table.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which campaigns drive the most sales, and which waste spend?',
      'Where is ACOS above my target?',
      'Which campaigns are limited by budget?',
      'How is campaign performance trending over time?',
    ],
    howTo: [
      PPC_TAB_STEPS.scope,
      'Add filters (for example ACOS above 30%) to narrow the list.',
      "Change a campaign's state or daily budget right in its cell.",
      PPC_TAB_STEPS.edit,
      PPC_TAB_STEPS.menu,
    ],
  },
  'ad-group': {
    title: 'Management · Ad Group',
    summary: 'Performance of each ad group inside your campaigns, with its default bid.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which ad groups convert best inside a campaign?',
      'Is a default bid too high for the sales it brings?',
      'Which ad groups get impressions but no clicks?',
    ],
    howTo: [
      PPC_TAB_STEPS.scope,
      'Filter by campaign to compare the ad groups inside it.',
      'Adjust the default bid in the cell.',
      PPC_TAB_STEPS.menu,
    ],
  },
  target: {
    title: 'Management · Target',
    summary:
      'Keywords and product targets with their bids and results — the level where most day-to-day bid optimization happens.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which keywords and product targets are profitable?',
      'Which targets spend without converting?',
      'Where should I raise or lower bids?',
      'How does each match type perform?',
    ],
    howTo: [
      PPC_TAB_STEPS.scope,
      'Sort by spend or ACOS to find the targets that matter most.',
      'Change a bid or pause a target directly in the table.',
      'Add new keyword or product targets from the toolbar.',
      PPC_TAB_STEPS.menu,
    ],
  },
  'negative-target': {
    title: 'Management · Negative Target',
    summary:
      'All negative keywords and negative product targets that stop your ads from showing on irrelevant searches or products.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which searches am I already blocking, and where?',
      'Are negatives set at the campaign or the ad-group level?',
      'Which negatives block keywords and which block products?',
    ],
    howTo: [
      'Choose a marketplace at the top. Negatives are not tied to dates, so the date range does not apply here.',
      'Filter by campaign, ad group, negative targeting type or level.',
      'Add negative keywords or products with the Add Negatives button.',
      'Select negatives to change their status (for example archive them) in bulk.',
    ],
  },
  'search-term': {
    title: 'Management · Search Term',
    summary:
      'The actual customer searches that triggered your ads, with the target that matched them and the results they produced.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which customer searches bring sales I am not targeting yet?',
      'Which searches cost money but never convert?',
      'Which target matched each search?',
    ],
    howTo: [
      PPC_TAB_STEPS.scope,
      'Sort by sales to find winning searches, or by spend with zero orders to find waste.',
      'Turn a converting search into a new target.',
      'Add a wasteful search as a negative.',
    ],
  },
  ads: {
    title: 'Management · Ads',
    summary:
      'Performance of each ad — the advertised products (ASIN/SKU) and creatives in your campaigns.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which advertised products sell best from ads?',
      'Which products receive spend but little sales?',
      'Which ads are paused or not delivering?',
    ],
    howTo: [
      PPC_TAB_STEPS.scope,
      'Filter by campaign or product to focus the list.',
      'Enable or pause an ad from its state cell.',
      PPC_TAB_STEPS.menu,
    ],
  },
};

const PPC_TAB_METRICS = [
  {
    name: 'ACOS',
    description: 'Advertising cost of sales: spend ÷ ad sales. Lower is more efficient.',
  },
  { name: 'ROAS', description: 'Return on ad spend: ad sales ÷ spend.' },
  { name: 'CVR', description: 'Conversion rate: orders ÷ clicks.' },
  { name: 'CPC', description: 'Average cost per click.' },
];

// Negative Target lists negatives only — it has no performance columns to explain.
const TABS_WITHOUT_METRICS = new Set(['negative-target']);

const tabEntries = (routeName) =>
  Object.fromEntries(
    Object.entries(PPC_TABS).map(([tab, entry]) => [
      pageGuideKey(routeName, tab),
      TABS_WITHOUT_METRICS.has(tab) ? entry : { metrics: PPC_TAB_METRICS, ...entry },
    ]),
  );

export const PAGE_GUIDES = Object.freeze({
  ...(targetAcosEnabled ? {
    'ppc-performance-targets': {
      title: 'Performance Targets',
      summary: 'Set Target ACOS for your business profile, marketplaces and advertising profiles, and supply manual break-even economics where Seller SKU data is unavailable.',
      requires: PAGE_GUIDE_REQUIRES.ADS,
      questions: [
        'Which Target ACOS applies to each advertising profile?',
        'Where does a target inherit from, and who changed it?',
        'Which ASINs need manual break-even economics?',
        'When will a change appear in reports?',
      ],
      howTo: [
        'Set a business-profile target, then override it for a marketplace or advertising profile when needed. Owners and admins can edit; members can view.',
        'Use Clear to inherit from a parent. Open History to see every value, author and date; only the newest change can be undone.',
        'The first value covers all history. Later changes start from local today; the save message gives the date used. Reports update within a minute.',
        'Open the Break-even tab and choose a marketplace under Manual unit economics. Use a break-even percentage or unit costs for ASINs without a Seller SKU; manage Seller SKU costs in COGS.',
        'Save up to 1000 manual changes together. Any invalid row prevents the whole save and highlights the cells to fix.',
        'Choose whether break-even should count revenue lost on returns. Expand How it works for inheritance and calculation details.',
      ],
      metrics: [
        { name: 'Target ACOS', description: 'Your target advertising cost as a percentage of sales. Report-period targets are sales-weighted and can differ from today’s setting.' },
        { name: 'Target source', description: 'The nearest target scope with a value: target, ad group, campaign, product, advertising profile, marketplace or business profile. There is no system default.' },
        { name: 'Break-even ACOS', description: 'Advertising cost as a percentage of sales at which economics break even, from Seller SKU economics or a manual percentage or unit-cost entry.' },
        { name: 'Coverage', description: 'The share of the report with target or break-even economics data. Missing values are not zero.' },
      ],
    },
  } : {}),
  dashboard: {
    title: 'Overview',
    summary:
      'Your connected marketplaces at a glance: total sales, ad spend, TACoS and ROAS across your connected marketplaces, with one card per marketplace.',
    requires: PAGE_GUIDE_REQUIRES.NONE,
    questions: [
      'How are my total sales and ad spend trending compared with the previous period?',
      'How much of my sales do my ads cost me (TACoS), and what do they return (ROAS)?',
      'Which marketplace drives my sales right now?',
      'Is there anything waiting for my review, such as AI Actions?',
    ],
    howTo: [
      'Pick a date range and a display currency at the top — every figure follows them.',
      'Read the totals first: the arrow shows the change versus the previous period of the same length.',
      "Use a marketplace card's Open link to go to that marketplace's own dashboard.",
      'Connect another marketplace from the tiles below the cards.',
    ],
    metrics: [
      {
        name: 'Total sales',
        description:
          'Amazon store sales. Sales attributed to Amazon ads are part of store sales and are never added on top.',
      },
      { name: 'TACoS', description: 'Ad spend ÷ total sales. Lower is more efficient.' },
      { name: 'ROAS', description: 'Ad sales ÷ ad spend.' },
    ],
    tips: [
      'The last 1-2 days of Amazon seller sales may still be updating.',
    ],
  },
  'ppc-dashboard': {
    title: 'Dashboard',
    summary:
      'A one-screen overview of your Amazon advertising: headline KPIs with the change versus the previous period, trends, spend mix by ad type, and cards that flag problems.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'How are spend, sales and ACOS trending compared with the previous period?',
      'How is spend split between Sponsored Products, Brands and Display?',
      'Which campaigns perform best right now?',
      'Where am I wasting spend on clicks that never convert?',
      'Which advertised products are out of stock?',
    ],
    howTo: [
      'Pick a marketplace and a date range at the top — every card follows them.',
      'Read the KPI cards first: the arrow shows the change versus the previous period of the same length.',
      "Use the open icon in a card's header, such as Top Campaigns or Wasted Spend, to open the matching report, already filtered.",
      'Use the settings button to show, hide and reorder dashboard sections.',
    ],
    metrics: [
      {
        name: 'Total Spend / Total Sales',
        description: 'Ad spend and the sales attributed to your ads in the period.',
      },
      { name: 'ACOS', description: 'Spend ÷ ad sales. Lower is more efficient.' },
      { name: 'CTR / CVR', description: 'Clicks ÷ impressions, and orders ÷ clicks.' },
      { name: 'CPC', description: 'Average cost per click.' },
    ],
  },

  ...tabEntries('ppc-analytics-tab'),
  // Management v2 (dev-only OLAP rewrite) uses the same tabs and answers the same questions.
  ...tabEntries('ppc-management-tab'),

  'ppc-unified-report': {
    title: 'Advertised Product',
    summary:
      "Amazon's unified ads report across Sponsored Products, Brands and Display, at the level of the advertised product. Break the numbers down by campaign, ad group, target, placement, device or supply source.",
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which advertised products earn the most from ads, and at what ACOS?',
      'Which campaign types, placements or devices perform best?',
      'How much of my ad sales comes from the advertised product versus other products (halo)?',
      "How did a product's ad performance change week over week?",
    ],
    howTo: [
      OLAP_PICK_SCOPE,
      'Drag dimensions (campaign, placement, device…) into the grouping bar to break the data down.',
      'Turn metrics on or off in the Metrics panel; the chart can show up to six at once.',
      'Filter any column, for example ACOS above 30% or a specific campaign type.',
      OLAP_SAVE_VIEW,
    ],
    metrics: [
      {
        name: 'Sales (Promoted) / Sales (Halo)',
        description:
          'Sales of the advertised product itself versus other products bought after the ad interaction.',
      },
      { name: 'ACOS', description: 'Spend ÷ sales.' },
      ...(targetAcosEnabled ? [
        { name: 'Target ACOS', description: 'Sales-weighted target for this period. Rows without a target have no ACOS colour grading. Weekly/monthly reports use the period-start target.' },
        { name: 'Target source', description: 'Where the target comes from; Set here marks this row’s level, Mixed marks different levels below.' },
        { name: 'ACOS > Target Loss', description: 'Spend above allowed spend on rows with a target; rows without a target are left out.' },
        { name: 'Break-even ACOS', description: 'Margin before ads as a percentage of covered sales. Hidden by default; enable it in Metrics → Break-even. Coverage shows missing economics.' },
      ] : [{
        name: 'ACOS > 30% Loss',
        description: 'Spend above 30% of sales — how much you over-spent against a 30% ACOS target.',
      }]),
      {
        name: 'vImpr. / VCPM',
        description: 'Viewable impressions and the cost per 1,000 of them.',
      },
    ],
    tips: [
      'Right-click any row to edit the entity, add negatives, or jump to its search terms or history.',
    ],
  },

  'ppc-unified-search-terms': {
    title: 'Search Terms',
    summary:
      'The customer searches that triggered your ads, with the target that matched each one and the spend and sales it produced.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which searches convert well but are not yet exact-match targets?',
      'Which searches spend money without a single order?',
      'Which targets trigger which searches?',
      'Which search terms are already negated?',
    ],
    howTo: [
      OLAP_PICK_SCOPE,
      'Use the "Search Terms Triggering Targets" configuration to see each target with the searches it matched.',
      'Sort by spend and filter orders = 0 to find wasted spend.',
      'Right-click a search term to add it as a negative or as a new target.',
      OLAP_SAVE_VIEW,
    ],
    metrics: [
      { name: '∑ Negs', description: 'How many times the search term is already negated.' },
      { name: 'CVR', description: 'Orders ÷ clicks — how well a search converts.' },
      { name: 'ACOS', description: 'Spend ÷ sales for that search.' },
    ],
  },

  'ppc-converted-product': {
    title: 'Converted Product',
    summary:
      'Which products shoppers actually bought after interacting with your ads — the advertised product or another product from your catalog (halo sales).',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which of my products sell because of ads for a different product?',
      'Does advertising one product pull sales to its variations or bundles?',
      'Which campaigns generate the most halo sales?',
      'How many orders come from customers new to my brand?',
    ],
    howTo: [
      OLAP_PICK_SCOPE,
      'Use "Advertised → Converted" to see, for each advertised product, what was actually bought.',
      'Use "By Converted Product" to rank the products that were purchased.',
      'Add New-to-Brand or Promoted vs Halo metrics from the Metrics panel.',
    ],
    tips: [
      'This report has conversion metrics only — no spend, impressions or clicks. Use Advertised Product for cost and ACOS.',
    ],
  },

  'ppc-traffic-conversion': {
    title: 'Traffic & Conversion',
    summary:
      'Ad traffic (impressions, clicks, spend) and conversions (orders, sales) by campaign, target and placement, down to the hour.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which placements — top of search, product pages, rest of search — convert best?',
      'At what hours of the day do my ads convert?',
      'Which targets bring traffic but no orders?',
      'How do placement bid adjustments affect results?',
    ],
    howTo: [
      OLAP_PICK_SCOPE,
      'Group by Placement to compare where your ads showed.',
      'Add the Hour dimension to switch to hourly data and find your best times of day.',
      'Try the "Product/CampaignType/Target" or "Target & Campaign (Date)" configurations.',
      'Right-click a row to edit it, add negatives, or open its search terms or history.',
    ],
  },

  'ppc-history': {
    title: 'History',
    summary:
      'A change log of your campaigns, ad groups, ads and targets: what changed, the old and new value, when, and who made the change.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'What changed in my account before performance moved?',
      'Who changed this bid or budget — a teammate, WisePPC or someone in Amazon?',
      'When was this campaign paused or created?',
    ],
    howTo: [
      'Pick a marketplace and a date range at the top.',
      'Changes are grouped by date, then by entity type — expand a group to see each change.',
      'Filter Change Source to separate WisePPC users, WisePPC automation and external (Amazon) changes.',
      'Filter by campaign, entity type or changed property to follow a single item.',
    ],
    tips: ['Right-click an entity on the other PPC reports to jump straight to its history here.'],
  },

  'ppc-creative-assets': {
    title: 'Creative Assets',
    summary:
      'The images and videos in your Amazon Ads Creative Assets library, with every ad and Brand Store page that uses each one.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which ads and campaigns use this image or video?',
      'Which assets are rejected or still pending moderation?',
      'Which assets does no ad and no Brand Store page use any more?',
      'Which assets sit on each page of my Brand Store?',
    ],
    howTo: [
      'Pick a marketplace at the top.',
      'Choose a configuration: Assets (each asset, then the campaigns, ad groups and ads using it), By campaign, By store, By asset ASIN, Moderation, Unused assets and more.',
      'Expand a row to walk down the tree; hover a thumbnail to see it larger.',
      'Use Flat for one row per asset and ad, to filter and export.',
    ],
    metrics: [
      {
        name: '# Ads / # Campaigns / # Store pages',
        description: 'How many ads, campaigns and Brand Store pages use the asset.',
      },
      {
        name: '# Asset ASINs / # Advertised ASINs',
        description:
          'How many ASINs the asset is tagged with, and how many the ad using it advertises. Click a count to list them.',
      },
    ],
    tips: [
      'This is the current library, not a time series: the catalog refreshes hourly.',
      'An asset Amazon deleted stays listed with State Deleted, so you can still see where it was used.',
      'Store pages are read from each store\'s draft edition, which may differ from what shoppers see.',
    ],
  },

  'ppc-ai-actions': {
    title: 'AI Actions',
    summary:
      'Changes to your Amazon Ads proposed by AI agents (for example an AI assistant connected with an MCP key). Nothing reaches Amazon until you approve it.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'What changes are AI agents proposing for my campaigns?',
      'Why did the agent propose each change?',
      'Which proposals were approved, declined or withdrawn?',
    ],
    howTo: [
      'Pick the marketplace whose proposals you want to review.',
      'Open the Open tab to see proposals waiting for a decision.',
      "Read each card's change and reasoning, then approve or decline it.",
      'Use the Approved, Declined, Cancelled and All tabs to review past decisions.',
    ],
  },

  'brand-analytics-search-catalog-shipping-competition': {
    title: 'Search Catalog · Competition',
    summary:
      "Your products' weekly search funnel (impressions, clicks, cart adds, purchases) split by shipping speed, next to the market demand for the search terms they compete in.",
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Do my products convert better or worse than the market for the same searches?',
      'Does faster shipping (same-day, 1-day, 2-day) lift my conversion?',
      'How big is the market my product competes in?',
      'Is my price above or below what shoppers pay in the market?',
    ],
    howTo: [
      'Pick a marketplace and a date range — data is weekly.',
      'The default configuration groups Parent → Product → Shipping Speed → Week.',
      'Compare each of your rates (Click, Cart Add, Conversion) with its Mkt. counterpart.',
      'Hover a column header for exactly how the number is calculated.',
    ],
    metrics: [
      {
        name: 'Mkt. Impressions / Clicks / Purchases',
        description:
          'Totals across all tracked search terms your product ranks for — the competitive demand pool.',
      },
      {
        name: 'Competing Terms',
        description: 'How many tracked search terms the product ranked for.',
      },
      {
        name: 'Mkt. Conversion Rate %',
        description:
          'Market purchases ÷ market clicks — the benchmark for your own conversion rate.',
      },
    ],
    tips: [
      'Brand Analytics data is available only for brands enrolled in Amazon Brand Registry.',
      'Market totals are overstated on group rows when two of your products share a search term; the rates stay correct.',
    ],
  },

  'brand-analytics-search-query': {
    title: 'Search Query Performance',
    summary:
      'For each search query that led shoppers to your products: how much the whole market searched, clicked and bought, and what share of that your products captured. Weekly.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Which search queries matter most for my products?',
      'What share of clicks and purchases do I win for each query?',
      'Where does my click or conversion rate fall behind the market?',
      'How is query volume trending week to week?',
    ],
    howTo: [
      'Pick a marketplace and a date range — data is weekly.',
      'Choose a configuration: "By Query × Product", "By Product x Query" or "By Week".',
      'Sort by Mkt. Query Volume to find the biggest queries, then read your share columns.',
      'Use the "ASIN vs Mkt." Δpp columns to spot where you under-perform the market.',
    ],
    metrics: [
      {
        name: 'Mkt. Query Volume',
        description: 'How many times shoppers searched the query on the marketplace.',
      },
      {
        name: 'ASIN Click / Purchase Share',
        description: "Your product's share of all clicks or purchases on that query.",
      },
      {
        name: 'ASIN vs Mkt. CVR Δpp',
        description: "Your conversion rate minus the market's, in percentage points.",
      },
    ],
    tips: ['Brand Analytics data is available only for brands enrolled in Amazon Brand Registry.'],
  },

  'brand-analytics-search-terms': {
    title: 'Top Search Terms',
    summary:
      "Amazon's most-searched terms across the whole marketplace, with the three products that won the most clicks for each. Market-wide data, updated weekly — the clicked products may be competitors.",
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'What are shoppers searching for most in my category?',
      'Which products dominate clicks and purchases for a search term?',
      'Do my products appear among the top three for important terms?',
      'Which rising terms should I target in my campaigns?',
    ],
    howTo: [
      'Pick a marketplace and a date range — data is weekly.',
      'Use "Top Search Terms" to rank terms by popularity (rank 1 = most searched).',
      'Use "By ASIN" to see which terms a product wins clicks for.',
      'Filter by department or search term to focus on your niche.',
    ],
    metrics: [
      {
        name: 'Search Frequency Rank',
        description: 'How popular the term is on the marketplace; 1 is the most searched.',
      },
      {
        name: 'Click Share % / Conversion Share %',
        description: "The product's share of all clicks or purchases for the term.",
      },
      {
        name: 'Conversion Index',
        description:
          'Conversion share ÷ click share — above 1 means the product converts better than average for that term.',
      },
    ],
    tips: [
      'Amazon reports ranks and shares only, not absolute clicks or purchases; totals across terms are not additive.',
    ],
  },

  'brand-analytics-brand-metrics': {
    title: 'Brand Metrics',
    summary:
      "How shoppers engage with your brand across the whole store (awareness, consideration and sales indexes, conversion, new-to-brand rate and customer counts), next to the category median and top performers. Weekly or monthly: a weekly row is the week ending Saturday, keyed by that week's Monday; a monthly row is a calendar month, keyed by its 1st.",
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'How does my brand rank against other brands in each category?',
      'Do I convert and win new customers better or worse than the category median?',
      'How far am I from the top performers in my category?',
      'Is my brand health improving week to week or month to month?',
    ],
    howTo: [
      'Pick a marketplace and a date range, then switch between Week (the default) and Month with the W/M toggle. The date range snaps to whole weeks or whole months.',
      'Rows form a tree: brand, then category tree, then the categories under it. Expand a row to go one level down.',
      "A category row shows that category's own numbers from Amazon. Brand, tree and Total rows add up (your own shopper counts) or average (everything else) the top-level categories for each week or month, then average those periods. Your own shopper counts on these rows are an upper bound when they span more than one top-level category: a shopper in two of them counts twice.",
      'Values are averages over the selected weeks or months; Periods reported says how many periods are behind each row.',
      'Open the Benchmarks configuration to put your brand next to the category median and top performers.',
      "Select any row, or leave none selected for the Total, to chart its weeks or months. The brand's own shopper counts add up across brands; everything else (rates, indexes, benchmarks, return on engagement) is averaged. Card headers show the average week or month.",
    ],
    metrics: [
      {
        name: 'Awareness / Consideration / Sales index',
        description: 'Your percentile among the brands in the category.',
      },
      {
        name: 'Category median / Top performers',
        description: "Amazon's benchmark for the same category and period.",
      },
      {
        name: 'Return on engagement',
        description:
          'The sales that historically followed an engagement, in the marketplace currency.',
      },
    ],
    tips: [
      "Category rows include every level of Amazon's browse tree, and a parent includes its children. Never add rows up.",
      'A blank value means Amazon withheld it because there was too little data. It does not mean zero.',
      'A category row shows the average over the selected weeks or months, not a total.',
      "Monthly data comes from Amazon's calendar-month report. Monthly shopper counts are unique within the month, never a roll-up of the weeks, so a month will not equal the sum or average of its weeks. The latest month appears a few days after it ends.",
      'These are all-shopper measures, not ad-attributed results.',
      'Header abbreviations: DPV = detail page view, ATC = add to cart, NTB = new-to-brand, HV = high-value, S&S = Subscribe & Save, ROE = return on engagement. Median / Top = category median / category top performers; Low / High = lower / upper end of Amazon\'s range.',
      'Brand Metrics is a beta Amazon Ads report for sellers in Brand Registry, in US, CA, MX, UK, DE, FR, IN, IT, ES, JP and AU.',
    ],
  },

  'brand-store-overview': {
    title: 'Brand Store',
    summary:
      'Traffic, engagement and sales of your Amazon Brand Store per day, with the Store Quality Score: page views, visits, visitors, dwell time, bounce rate, orders and sales, and how your dwell time and sales compare with similar stores. Pages and traffic sources are on the Traffic page.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'How many shoppers visit my Brand Store, and is that growing?',
      'How long do visitors stay, and how many leave straight away?',
      'How many orders and how much in sales come from store visitors?',
      'How does my store compare with similar stores on the Store Quality Score?',
    ],
    howTo: [
      'Pick a marketplace and a date range. Data is daily.',
      'Rows are grouped by store. Expand a store to see its days, newest first.',
      'Switch the chart between days, weeks and months. It shows page views, visits, orders and sales only.',
      'Open the Store Quality configuration for the quality score, your dwell time against similar stores, and trailing 60-day sales.',
    ],
    metrics: [
      {
        name: 'Page views / Visits',
        description: 'Store page views, and visits (one visit can view several pages).',
      },
      {
        name: 'Visitors / Day',
        description:
          "Amazon's daily count of unique visitors. Group rows show the average per day, never a sum.",
      },
      {
        name: 'Dwell time / Bounce rate',
        description:
          'Average seconds a visit spends on the store, and the share of visits that left without engaging, as Amazon reports them each day.',
      },
      {
        name: '≈ Dwell time / ≈ Bounce rate',
        description:
          'Approximate dwell time and bounce rate over several days: the daily values weighted by visits, so a store row, a week or a month gets a meaningful figure.',
      },
      {
        name: 'Orders / Units / Sales',
        description:
          'Purchases by store visitors within 14 days of their visit (Amazon estimates).',
      },
      {
        name: 'ASIN Views / Clicks (total)',
        description:
          'Views and clicks of all the products on the store, added up. Hidden by default; they are a different unit from page views.',
      },
      {
        name: 'Store Quality Score',
        description:
          "Amazon's rating of the store (Low, Medium, High), with your dwell time and trailing 60-day sales against similar (peer) stores.",
      },
    ],
    tips: [
      'Orders, units and sales are Amazon estimates attributed to store visits within 14 days, so the last 14 days change every night.',
      'Visitors, dwell time, bounce rate and the quality columns are not added up: group rows show the average over the selected days, and they have no total and no chart.',
      'The ≈ dwell time and ≈ bounce rate are approximations weighted by visits: bounce rate is usually within 1-2% of Amazon\'s figure and dwell time about 3% low. They have a total and can be charted.',
      'ASIN Views and ASIN Clicks (total) count the products on the store, a different unit from page views and visits. Never add or compare them.',
      'Sales, Last 60 Days is a rolling 60-day figure as of each day. Never add days together.',
      'A blank value means Amazon gave no value that day. It does not mean zero.',
      'History starts in September 2025 (Amazon keeps about 13 months). Data usually arrives two days late.',
      'Covers the Brand Stores of sellers in Brand Registry; vendor stores are not included. Store names come from the registered brand.',
    ],
  },

  'brand-store-traffic': {
    title: 'Brand Store Traffic',
    summary:
      'Where the visits to your Amazon Brand Store land and where they come from, per day: each page of the store split by traffic source (Amazon advertising, organic, other), with views, visits, dwell time, bounce rate, orders and sales.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which pages of my Brand Store get the most views and visits?',
      'How much of my Brand Store traffic comes from Amazon ads, and how much is organic?',
      'Which pages and sources keep visitors and which lose them straight away?',
      'Which pages and sources lead to orders and sales?',
    ],
    howTo: [
      'Pick a marketplace and a date range. Data is daily.',
      'Start from By Page, or open By Source, Page x Source or Daily to group the same rows another way. Expand a row to see what it is made of, newest days first.',
      'Switch the chart between days, weeks and months. It shows page views, visits and sales; pick the ≈ columns from the metric list to chart them.',
      'Group by Page ID instead of Page to tell apart two pages that share a title.',
    ],
    metrics: [
      {
        name: 'Page views / Visits',
        description: 'Views of the page from the source, and visits that viewed it.',
      },
      {
        name: '≈ Dwell time / ≈ Bounce rate',
        description:
          "Approximate average seconds per visit and share of visits that left without engaging, for any group of rows: Amazon's values for each page, source and day, weighted by visits.",
      },
      {
        name: 'Dwell time / Bounce rate',
        description:
          "Amazon's own value for one page, source and day. They are blank on the remainder rows and cannot be added up.",
      },
      {
        name: 'Orders / Units / Sales',
        description:
          'Purchases by store visitors within 14 days of their visit (Amazon estimates).',
      },
    ],
    tips: [
      'Views, visits, orders, units and sales add up across all rows to the Brand Store Overview figures. Amazon hides small page and source groups, so the Row Kind column marks the rows that hold what is left over (Filter remainder, Unattributed, Store remainder). Never filter them out of a total.',
      'Unattributed is a traffic source of its own: views and visits that Amazon counts for a page or the store but cannot place on one source.',
      'The ≈ dwell time and ≈ bounce rate are approximations: bounce rate is usually within 1-2% of Amazon\'s own figure and dwell time about 3% low, and a small group can be 20-50% off. Dwell time and bounce rate per row are never added up and have no total.',
      'Orders, units and sales are Amazon estimates attributed to store visits within 14 days, so the last 14 days change every night.',
      'Page titles come from the published page list. A page Amazon no longer lists has a blank title.',
      'History starts in September 2025 (Amazon keeps about 13 months). Data usually arrives two days late.',
      'Covers the Brand Stores of sellers in Brand Registry; vendor stores are not included.',
    ],
  },

  'brand-store-products': {
    title: 'Brand Store Products',
    summary:
      'How each product (ASIN) performed on your Amazon Brand Store per day: views, clicks, add to carts, orders, units and prices, with click, conversion and in-stock rates.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which products get the most views and clicks on my Brand Store?',
      'Which products turn store clicks into units?',
      'Are my best-viewed products in stock when shoppers see them?',
    ],
    howTo: [
      'Pick a marketplace and a date range. Data is daily.',
      'Rows are grouped by store, then parent product, then product. Expand a product to see its days, newest first.',
      'Open the By Product configuration to see each product across all stores.',
      'The chart shows views, clicks, units and orders only.',
    ],
    metrics: [
      {
        name: 'ASIN Views / Clicks',
        description: 'How often the ASIN was viewed and clicked on the store.',
      },
      {
        name: 'Click Rate / Conversion Rate / In-Stock Rate',
        description:
          'Clicks per view, units per click (units, not orders), and in-stock views per view. Always worked out from the summed counts of the rows in the group.',
      },
      {
        name: 'Orders / Units / Add to Carts',
        description:
          'Purchases of the ASIN by store visitors within 14 days of their visit (Amazon estimates).',
      },
      {
        name: 'Avg. Sale Price / Avg. In-Stock Price',
        description:
          'Average price over the days the ASIN sold, and over the days it had an in-stock view.',
      },
      {
        name: '≈ Avg. Sale Price / ≈ In-Stock Price',
        description:
          'Approximate prices over any group of rows, weighted by units sold and by in-stock views, so a busy day counts more than a quiet one.',
      },
    ],
    tips: [
      'ASIN Views are a different unit from the page views on the Brand Store Overview. Never compare or add them.',
      'Orders and units are Amazon per-ASIN estimates attributed to store visits within 14 days, so the last 14 days change every night. They do not add up to the store orders on the Brand Store Overview.',
      'Rates are never added or averaged across rows; a group shows the rate of its totals.',
      'Prices are not added up: group rows show the average over the days that have a value, with no total and no chart. A blank value does not mean zero.',
      'The ≈ prices are weighted by units sold and in-stock views, so they have a total and can be charted. They are approximations of the average price shoppers saw.',
      'This breakdown is still filling in backwards, so it can start later than the Brand Store Overview. Data usually arrives two days late.',
      'Covers the Brand Stores of sellers in Brand Registry; vendor stores are not included.',
    ],
  },

  'brand-store-engagement': {
    title: 'Brand Store Engagement',
    summary:
      'How visitors engage with your Amazon Brand Store by day, week or month: dwell time and bounce rate for all traffic and for each traffic source, plus new-to-store visitors.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Are visitors staying longer on my Brand Store than before?',
      'Is my bounce rate getting better or worse?',
      'Do visitors from ads engage differently from organic visitors?',
      'How many visitors are new to my store each week or month?',
    ],
    howTo: [
      'Pick a marketplace, a date range and Day, Week or Month. Each is Amazon\'s own value for that period, not an average of the days.',
      'Rows are grouped by store. Expand a store to see its periods, newest first.',
      'Open the Source breakdown configuration to see dwell time and bounce rate for all traffic, Ads, Organic and Other side by side.',
      'The chart shows new-to-store visitors per period.',
    ],
    metrics: [
      {
        name: 'Dwell time / Bounce rate',
        description:
          'Average seconds a visit spends on the store, and the share of visits that left without engaging. Shown for all traffic and for each source (Ads, Organic, Other). A source is blank when Amazon gives no figure for it.',
      },
      {
        name: 'New to Store',
        description:
          'Unique visitors new to the store in the period, for all traffic. Amazon gives no split by source. It adds up across periods and stores.',
      },
    ],
    tips: [
      'Day is Amazon\'s daily values. Week is ISO weeks, Monday to Sunday. Month is calendar months. Week and month values come straight from Amazon; they are not averages of the days.',
      'Dwell time and bounce rate are never added: a store row shows the average over its periods, with no total and no chart. An average over several periods is a plain mean of the period values. The ≈ columns on the Brand Store Overview and Traffic pages are visit-weighted approximations of these exact values.',
      'New to Store has no per-source split, because Amazon returns none. It adds up across periods and stores.',
      'The current week and month are partial (through yesterday) and keep changing until they close. Period Complete shows which rows are final.',
      'The page works in whole weeks or months: the date picker snaps the range to the first day of its first week or month. A shared link whose range starts mid-week or mid-month is not snapped and leaves that period out.',
      'Covers the Brand Stores of sellers in Brand Registry; vendor stores are not included.',
    ],
  },

  'brand-store-page-content': {
    title: 'Brand Store Page Content',
    summary:
      'The pages of your Amazon Brand Store as the store editor holds them: title, type, template, widgets and the products each page shows.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which pages does each edition of my Brand Store have?',
      'Which ASINs does each page show?',
      'When did a page last change?',
    ],
    howTo: [
      'Rows are grouped by store, then edition. Expand an edition to see its pages.',
      'Add the ASINs, version and last content change columns from the Dimensions panel as needed.',
      'There is no date range: this is the current content, not a time series. See Brand Store Change History for what changed and when.',
    ],
    metrics: [
      {
        name: 'Widgets / ASIN Count / ASINs',
        description:
          'How many widgets the page has, how many unique ASINs it shows, and which ones.',
      },
      {
        name: 'Version / Last Content Change',
        description:
          "The page's version in the draft, and when a snapshot last detected a change to its content.",
      },
    ],
    tips: [
      'This is the DRAFT: Amazon only exposes draft versions of a store, so a row is what the store editor holds, not necessarily what shoppers see.',
      'The snapshot is refreshed nightly and after each edit made through WisePPC (MCP).',
      'Counts and versions are per page and are never added up.',
      'Covers the Brand Stores of sellers in Brand Registry; vendor stores are not included.',
    ],
  },

  'brand-store-changes': {
    title: 'Brand Store Change History',
    summary:
      'A log of the changes detected on the pages of your Amazon Brand Store: what changed, which ASINs were added or removed, and whether the change was made in WisePPC or outside it.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'What changed on my Brand Store pages recently?',
      'Which ASINs were added to or removed from a page?',
      'Was a change made through WisePPC or outside it?',
    ],
    howTo: [
      'Pick a marketplace and a date range. The range filters on the day the change was detected.',
      'The newest change is first. Group by Store, Page, Change Type or Source to browse.',
      'Source tells a change made in WisePPC (wiseppc_mcp, wiseppc_user) from one made outside it (external); the user and mutation columns are set for WisePPC changes.',
    ],
    tips: [
      'Detected At is when a snapshot noticed the change (nightly, or right after an edit made through WisePPC), not when the edit was made.',
      'This logs changes to the DRAFT pages, which is what the store editor holds, not necessarily what shoppers see.',
      'Versions and ASIN counts are per change and are never added up. There are no totals.',
      'Covers the Brand Stores of sellers in Brand Registry; vendor stores are not included.',
    ],
  },

  'attribution-analytics': {
    title: 'Attribution Performance',
    summary:
      'How your off-Amazon traffic (Google, Meta, TikTok, email, your own site) performs on Amazon: click-throughs, detail page views, add-to-carts, orders and sales within 14 days of a click on an Amazon Attribution tag, by campaign, ad group, publisher and tagged link. Refreshed nightly from Amazon.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which off-Amazon channels and publishers drive sales on Amazon?',
      'Which campaigns, ad groups and tagged links convert best?',
      'How much do my other products sell (brand halo) thanks to my external traffic?',
      'How many new-to-brand customers does my external traffic win?',
      'How is my off-Amazon traffic trending month to month?',
    ],
    howTo: [
      OLAP_PICK_SCOPE,
      'Attribution is set up per marketplace. If the selected marketplace has no Attribution data, the page says so and lists the marketplaces that do.',
      'Start from a configuration: By Campaign, By Publisher, By Landing ASIN or Monthly Trend. Each one drills down to the individual tagged link.',
      OLAP_SAVE_VIEW,
    ],
    metrics: [
      { name: 'Click-throughs', description: 'Clicks on the tagged link that landed on Amazon.' },
      {
        name: 'Sales',
        description: 'Sales of the promoted product within 14 days of a click, in the marketplace currency.',
      },
      {
        name: 'Total Sales',
        description:
          'Sales of the promoted product and your other products (brand halo) within 14 days of a click.',
      },
      {
        name: 'BRB Bonus',
        description: 'Brand Referral Bonus credited on this traffic. Zero unless you are enrolled in Brand Referral Bonus.',
      },
    ],
    tips: [
      'Numbers are a blended upper bound: the tag counts every visit that reaches the tagged Amazon link, including traffic the channel did not cause.',
      'Amazon hides any tagged link with fewer than 10 clicks. Its traffic stays on the ad group\'s "(below threshold)" row, so totals still match the Amazon console. Do not filter those rows out.',
      'Campaign and ad group names are the free text on your tags, not Amazon Ads campaigns.',
      'Amazon keeps updating recent days for a while; WisePPC re-reads them every night.',
      'To start using Attribution, turn it on in Amazon Ads (Measurement & reporting, then Amazon Attribution). WisePPC picks it up on the next nightly sync.',
    ],
  },
  'attribution-products': {
    title: 'Attribution Products',
    summary:
      'Which of your products shoppers bought after clicking an Amazon Attribution tag: the promoted product the link pointed at and your other products bought in the same session (brand halo), including new-to-brand orders and sales, by campaign, publisher, product, brand and category.',
    requires: PAGE_GUIDE_REQUIRES.ADS,
    questions: [
      'Which products sell after a click on my off-Amazon links?',
      'How much of the sales comes from the promoted product, and how much from my other products?',
      'Which brands and categories benefit most from my external traffic?',
      'Which publishers drive sales of a given product?',
    ],
    howTo: [
      OLAP_PICK_SCOPE,
      'Attribution is set up per marketplace. If the selected marketplace has no Attribution data, the page says so and lists the marketplaces that do.',
      'Start from a configuration: By Campaign, By Product, Promoted vs Brand Halo, By Brand & Category or By Publisher. Each one keeps Conversion Type in the grouping, so promoted and brand-halo sales stay apart.',
      OLAP_SAVE_VIEW,
    ],
    tips: [
      'Conversion Type: Promoted is the product the tagged link pointed at, Brand Halo is another of your products bought in the same session.',
      'There are no click-throughs here: Amazon reports clicks per link, not per product. Use the Performance page for clicks.',
      'Amazon returns product data for the last 90 days only, so dates older than that from before WisePPC started syncing your account appear on Performance but not here.',
      'Numbers are a blended upper bound: the tag counts every visit that reaches the tagged Amazon link, including traffic the channel did not cause.',
    ],
  },
  'business-reports-sales-traffic-by-asin-dk': {
    title: 'Sales & Traffic by ASIN',
    summary:
      'Your total Amazon sales and product-page traffic per child ASIN and day — all sales, not just ads — including Buy Box and B2B figures.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Which products get the most sessions and page views?',
      'How well do product pages turn visits into orders?',
      'Am I winning the Buy Box?',
      'How much of my business comes from B2B customers?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'Use "By Product" to group child ASINs under their parent, or "By Product (no parent)" for a flat list.',
      'Chart sessions and units ordered together to see how traffic turns into sales.',
      'Filter by product to follow one ASIN day by day.',
    ],
    metrics: [
      {
        name: 'Sessions / Page Views',
        description: 'Visits to the product page, and total page views.',
      },
      {
        name: 'Unit Session %',
        description: 'Units ordered ÷ sessions — your product-page conversion rate.',
      },
      { name: 'Buy Box %', description: 'Share of page views where you held the Buy Box.' },
    ],
  },

  'finance-economics': {
    title: 'Economics / P&L',
    summary:
      'Profit and loss per SKU and day: sales, refunds, Amazon fees and ad spend combined into net proceeds and net profit.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Which products actually make money after fees and ads?',
      'How much do Amazon fees and refunds take from each sale?',
      'What share of my sales is organic versus from ads (TACOS)?',
      'How is my margin trending day by day?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'Start with "By Product" to rank products by net profit, or "By Day" to see the trend.',
      'Open an Amazon Fees cell to see the fee-by-fee breakdown behind it.',
      'Filter Cost Completeness to FULL to look only at fully costed rows.',
    ],
    metrics: [
      { name: 'Net Proceeds', description: 'Net sales minus Amazon fees and ad spend.' },
      {
        name: 'Net Profit',
        description:
          'Net proceeds minus your product cost (COGS). Where no cost is set it equals net proceeds.',
      },
      { name: 'TACOS %', description: 'Total ad spend ÷ total sales, including organic sales.' },
    ],
    tips: [
      'Amounts are per currency — keep Currency in view when a marketplace sells in more than one.',
      'Ad spend that cannot be tied to a product shows as a separate "Unallocated ads" row.',
    ],
  },

  'finance-transactions': {
    title: 'Transactions Ledger',
    summary:
      'Every financial transaction Amazon posted to your seller account — shipments, refunds, fees, reimbursements and payouts — grouped by settlement.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'How much did Amazon pay out, and when?',
      'Which amounts are still deferred and not yet released?',
      'What fees and refunds were charged against an order?',
      'Which transaction types move the most money?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'The default configuration groups by account, settlement and transaction; switch to "By Day", "By Marketplace" or "By Settlement Group".',
      'Filter Transaction Type (for example Refund or ServiceFee) to isolate one kind of charge.',
      'Filter Order ID to see every transaction tied to one order.',
    ],
    metrics: [
      { name: 'Disbursed', description: 'Funds Amazon transferred to your bank.' },
      {
        name: 'Released / Deferred',
        description: 'Amounts available for payout versus amounts Amazon is still holding.',
      },
    ],
    tips: ['Credits are positive and fees or refunds are negative.'],
  },

  'finance-cogs': {
    title: 'Seller COGS',
    summary:
      'Your product cost (COGS) per SKU — vendor, shipping and additional cost — which Economics / P&L uses to turn net proceeds into net profit.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Which SKUs still have no cost set?',
      'What does each product cost me, and from which date?',
      'Is a cost entered by hand or filled in from Amazon?',
    ],
    howTo: [
      'Pick a marketplace.',
      'Use "By Product Parent" to work through a variation family, or "Flat (all SKUs)" to see every SKU on its own row.',
      'Filter COGS Set to No to list the SKUs that still need a cost.',
      'Use Import to upload costs for many SKUs at once, or Export to get the current list.',
    ],
    metrics: [
      {
        name: 'Total Cost',
        description: 'Vendor cost + shipping cost + additional cost for one unit.',
      },
    ],
    tips: ['Each cost applies from its Effective Date onward, so earlier days keep the older cost.'],
  },

  'inventory-consolidated': {
    title: 'Consolidated Inventory',
    summary:
      'Stock for every SKU across FBA, AWD and your own warehouse in one table — where the units are, what they are worth and how long they will last.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'How many units do I have in total, and where are they?',
      'How much stock is reserved, inbound or unfulfillable?',
      'Which products are about to run out?',
      'What is my inventory worth at cost?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'Start with "By Product Parent", or "By Availability" to group products by whether they can be bought right now.',
      'Each row shows the latest stock observed in the date range; the chart shows how it moved day by day.',
    ],
    metrics: [
      {
        name: 'Grand Total',
        description: 'FBA total plus AWD available and AWD inbound stock.',
      },
      { name: 'FBA Total', description: 'Every unit Amazon holds or is receiving for you.' },
    ],
    tips: ['Costs need a COGS entry for the SKU — set them on the Seller COGS page.'],
  },

  'inventory-orders': {
    title: 'Orders',
    summary:
      'Every Amazon order from your Seller Central orders report — what sold, through which channel, and when.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'How many orders and units did I sell, day by day?',
      'How do FBA and merchant-fulfilled orders compare?',
      'What did a specific order contain?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'Use "By Day" for the trend, "By Channel" to split FBA from merchant-fulfilled, or "By Order" for one row per order.',
      'Search or filter the grid to find a single order.',
    ],
  },

  'inventory-mcf-orders': {
    title: 'MCF Orders',
    summary:
      'Multi-Channel Fulfillment orders — orders from your other sales channels that Amazon ships from your FBA stock.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Which MCF orders are still pending, shipped or cancelled?',
      'Which products go out through MCF most?',
      'What is in a specific MCF order?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'Start with "By Status" to see what is open, or "By Product" for the SKUs shipped through MCF.',
      'Switch to "Orders (flat list)" to see each order line item.',
    ],
  },

  'inventory-fba-returns': {
    title: 'FBA Returns',
    summary:
      'Customer returns Amazon received at its fulfillment centers — which products come back, why, and in what condition.',
    requires: PAGE_GUIDE_REQUIRES.SP,
    questions: [
      'Which products are returned most?',
      'What reasons do customers give for returns?',
      'Are returns rising or falling over time?',
    ],
    howTo: [
      'Pick a marketplace and a date range.',
      'Use "By Reason" to see why products come back, "By Product" for the most-returned SKUs, or "By Day" for the trend.',
      'Filter the grid by SKU or ASIN to focus on one product.',
    ],
  },
});

// Shown by EmptyProfile on a route with no entry of its own. Never offered from the
// header ("About this page" appears only for authored entries).
export const FALLBACK_PAGE_GUIDE = Object.freeze({
  title: 'About this page',
  summary:
    'This report is built from your Amazon data. It fills in automatically once your accounts are connected and the first sync has finished.',
  requires: PAGE_GUIDE_REQUIRES.ADS,
  questions: [
    'How are my key metrics trending over time?',
    'Which campaigns, products or items drive the results?',
    'Where can I cut waste or grow sales?',
  ],
  howTo: [
    OLAP_PICK_SCOPE,
    'Group, filter and sort the table to build the view you need.',
    'Switch between the ready-made configurations to see the data from different angles.',
  ],
});

// eBay and Shopify are development-only (see envOnly in the nav), so the authored copy above
// names only Amazon. On development these entries swap in the wording that also names eBay and Shopify.
const DEVELOPMENT_GUIDE_TEXT = {
  dashboard: {
    summary:
      'Your connected marketplaces at a glance: total sales, ad spend, TACoS and ROAS across Amazon, eBay and Shopify, with one card per marketplace.',
    metrics: {
      'Total sales':
        'Amazon store sales plus eBay and Shopify sales. Sales attributed to Amazon ads are part of store sales and are never added on top.',
    },
  },
};

const forDevelopment = (key, entry) => {
  const text = DEVELOPMENT_GUIDE_TEXT[key];
  if (!text || !entry) return entry;
  return {
    ...entry,
    summary: text.summary ?? entry.summary,
    metrics: entry.metrics?.map((metric) =>
      text.metrics?.[metric.name] ? { ...metric, description: text.metrics[metric.name] } : metric,
    ),
  };
};

export const findPageGuide = (route, { isDevelopment = false } = {}) => {
  if (!route?.name) return null;
  const tab = route.params?.tab;
  const key = PAGE_GUIDES[pageGuideKey(route.name, tab)] ? pageGuideKey(route.name, tab) : route.name;
  const entry = PAGE_GUIDES[key] || null;
  return isDevelopment ? forDevelopment(key, entry) : entry;
};

export const resolvePageGuide = (route, options) => findPageGuide(route, options) || FALLBACK_PAGE_GUIDE;
